<script>
	/**
	 * This top-level route displays a list of assignments for all the teacher's
	 * classes.
	 *
	 * PROPS
	 * - route-context - the context from page.js
	 *
	 * SUB ROUTES
	 *
	 * None. Switching article tabs from current to past simply results in a different
	 * query being sent to the server. There are no sub-routes per se.
	 */
	const Q = require('q');
	const _ = require('underscore');
	const moment = require('moment');
	const page = require('page');

	const classService = require('services/classService');
	const utilities = require('utilities');
	const model = require('model/model');

	const studentGroupUtilities = require('utilities/studentGroupUtilities');
	const studentProgressUtilities = require('utilities/studentProgressUtilities');

	const surveyUtilities = require('utilities/surveyUtilities');
	const getCVAAssignments = surveyUtilities.getCVAAssignments;

	const classUtilities = require('utilities/classUtilities');
	const sortStudentsByName = classUtilities.sortStudentsByName;
	const CLASS_STATUS_MAP = classUtilities.STATUS_MAP;
	const fetchRoster = classUtilities.fetchRoster;

	const assignmentUtilities = require('utilities/assignmentUtilities');
	const ASSIGNMENT_STATUS = assignmentUtilities.STATUS;
	const ASSIGNMENT_ACTIONS = assignmentUtilities.ACTIONS;
	const fetchAssignments = assignmentUtilities.fetchAssignments;

	const {
		fireEvent,
		events: { PRINT, EXPORT_CSV, NEXT_STUDENT, PREVIOUS_STUDENT }
	} = require('utilities/analyticsUtilities');

	const csvUtilities = require('utilities/csvUtilities');
	const downloadCSV = csvUtilities.downloadCSV;

	const { assetUrl } = require('utilities/urlUtilities');

	const studentKnowledgeInventoryUtilities = require('utilities/studentKnowledgeInventoryUtilities');
	const getSKIAssignments = studentKnowledgeInventoryUtilities.getSKIAssignments;

	const ASSIGNMENTS = 'assignments';
	const STUDENT_PROGRESS = 'progress';
	const CLASS_BOK = 'cbok';
	const RMS = 'rms';
	const STATUS_ACTIVE = ASSIGNMENT_STATUS.ACTIVE;
	const STATUS_DONE = ASSIGNMENT_STATUS.DONE;
	const STATUS_DELETED = ASSIGNMENT_STATUS.DELETED;
	const STATUS_FUTURE = ASSIGNMENT_STATUS.FUTURE;

	const INDIVIDUAL_STUDENT_PROGRESS = 'isp-progress';
	const BBOK_PROGRESS = 'bbok-progress';
	const LIBRARY_PROGRESS = 'library-progress';

	const ASSIGNMENT_ACTION_BEGIN = ASSIGNMENT_ACTIONS.ASSIGNMENT_ACTION_BEGIN;
	const ASSIGNMENT_ACTION_END = ASSIGNMENT_ACTIONS.ASSIGNMENT_ACTION_END;

	const { teacherGetStudentResearchAssessments } = require('utilities/studentResearchAssessmentUtilities');

	module.exports = {
		data() {
			return {
				inProgressAssignments: [],
				pastAssignments: [],
				assignments: null,
				metaData: {},
				assignmentForDeletion: null,
				assignmentsLoading: false,
				classesLoading: false,
				actionInProgress: false,
				filteredAssignments: null,
				assignmentsLoaded: false,
				filterClassChoices: [],
				classByCode: {},
				currentTime: new Date().getTime() / 1000,
				ASSIGNMENTS,
				STATUS_ACTIVE,
				STATUS_DONE,
				STATUS_DELETED,
				STATUS_FUTURE,
				STUDENT_PROGRESS,
				CLASS_BOK,
				RMS,
				LIBRARY_PROGRESS,
				INDIVIDUAL_STUDENT_PROGRESS,
				BBOK_PROGRESS,
				loadingGroups: false,
				resultsHash: null,
				studentNames: {},
				targetAssignmentProduct: ''
			};
		},
		components: {
			'assignment-close-modal': require('components/assignment-close-modal.vue').default,
			'assignment-delete-modal': require('components/assignment-delete-modal.vue').default,
			'change-deadline-modal': require('components/change-deadline-modal.vue').default,
			'show-results-modal': require('components/show-results-modal.vue').default,
			'assignments-list': require('components/assignments-list.vue').default,
			'student-progress': require('components/student-progress.vue').default,
			'individual-student-progress': require('components/individual-student-progress.vue').default,
			'big-book-of-knowledge': require('components/big-book-of-knowledge.vue').default,
			'class-book-of-knowledge': require('components/class-book-of-knowledge.vue').default,
			'student-library-individual-results': require('components/student-library-individual-results.vue').default,
			'tab-container': require('components/tab-container.vue').default,
			'student-knowledge-inventory': require('components/student-knowledge-inventory.vue').default,
			pagination: require('components/pagination.vue').default,
			'butter-bar': require('components/butter-bar-promo.vue').default,
			'sra-status-modal': require('components/sra-status-modal.vue').default
		},
		modelData: {
			assignmentTab: 'assignmentTab',
			assignmentListTab: 'assignmentListTab',
			classCode: 'filterClassCode',
			studentId: 'studentId',
			roster: 'roster',
			cvaAssignments: 'cvaAssignments',
			currentClassAssignments: 'assignments',
			studentProgressTab: 'studentProgressTab',
			studentProgressMonth: 'studentProgressMonth',
			isp: 'isp',
			individualStudentProgressExportData: 'ispExportData',
			localGCAccessToken: 'localGCAccessToken',
			studentGroups: 'studentGroups',
			skiAssignments: 'skiAssignments',
			userContext: 'userContext',
			researchAssessments: 'researchAssessments'
		},
		computed: {
			progressStudentName() {
				return this.studentNames[this.studentId];
			},
			progressStudent() {
				let currentRoster = this.currentRoster;
				let studentId = this.studentId;

				if (currentRoster) {
					return currentRoster[studentId];
				}
				return null;
			},
			progressMonthList() {
				const userContext = this.userContext;
				const currentClass = this.currentClass;
				let createdTS = 0;
				let monthList;

				if (userContext && userContext.created) {
					createdTS = userContext.created * 1000;
				} else if (currentClass && currentClass.created) {
					createdTS = currentClass.created * 1000;
				}
				if (typeof createdTS === 'number') {
					monthList = studentProgressUtilities.getMonthList(createdTS);

					monthList.push({
						text: 'See All',
						value: 'all'
					});
				}

				return monthList;
			},
			currentClass() {
				let classCode = this.filterClassCode;
				let classByCode = this.classByCode;

				if (!classCode || !classByCode) {
					return null;
				}

				return classByCode[classCode];
			},
			className() {
				let classes = this.filterClassChoices;
				let classCode = this.filterClassCode;
				let name = null;

				if (classes && classCode) {
					classes.every(c => {
						if (classCode === c.value) {
							name = c.text;
							return false;
						}
						return true;
					});
				}
				return name;
			},
			currentRoster() {
				let classCode = this.filterClassCode;
				let roster = this.roster;

				if (roster && classCode && roster[classCode]) {
					return roster[classCode].studentsById;
				}
				return null;
			},
			currentRosterKeys() {
				let currentRoster = this.currentRoster;
				let list = [];

				if (currentRoster) {
					list = Object.keys(currentRoster)
						.map(suid => currentRoster[suid])
						.sort(sortStudentsByName)
						.map(student => student.uid);
				}

				return list;
			},
			currentStudentIndex() {
				let list = this.currentRosterKeys;
				let studentId = this.studentId;

				if (studentId && list) {
					return list.indexOf(studentId);
				}
				return -1;
			},
			isLoading() {
				let assignmentsLoaded = this.assignmentsLoaded;
				let assignmentsLoading = this.assignmentsLoading;
				let classesLoading = this.classesLoading;
				let actionInProgress = this.actionInProgress;
				let loadingGroups = this.loadingGroups;

				return !assignmentsLoaded || assignmentsLoading || actionInProgress || loadingGroups || classesLoading;
			},
			cvaAssignment() {
				const classCode = this.filterClassCode;
				const assignmentTab = this.assignmentTab;
				const assignmentListTab = this.assignmentListTab;
				const cvaAssignments = this.cvaAssignments || {};
				const possibleAssignment = cvaAssignments[classCode];
				let cvaAssignment = null;

				if (!classCode || !assignmentTab || !possibleAssignment || assignmentListTab === STATUS_DONE) {
					return null;
				}

				if (possibleAssignment && possibleAssignment.s === assignmentListTab) {
					cvaAssignment = possibleAssignment;
				}

				return cvaAssignment;
			},
			cvaDate() {
				let cvaAssignment = this.cvaAssignment;
				let cvaDate = null;
				if (!cvaAssignment || cvaAssignment.s === STATUS_DONE) {
					return null;
				}
				if (cvaAssignment.s === STATUS_ACTIVE) {
					cvaDate = 'today';
				} else {
					cvaDate = moment(cvaAssignment.d * 1000).format('MMMM Do');
				}
				return cvaDate;
			},
			skiAssignment() {
				let skiAssignments = this.skiAssignments;
				let assignment = null;

				if (skiAssignments && skiAssignments.length) {
					skiAssignments.some(s => {
						if (!s.ed) {
							assignment = s;
							return true;
						}
						return false;
					});

					if (!assignment) {
						assignment = skiAssignments[skiAssignments.length - 1];
					}
				}

				return assignment;
			},
			skiScheduledDate() {
				let skiAssignment = this.skiAssignment;
				let now = new Date().getTime();

				// If RMS is scheduled for the future
				return skiAssignment && !skiAssignment.ed && skiAssignment.d * 1000 > now && skiAssignment.d;
			},
			showIndividualStudentProgress() {
				return this.assignmentTab !== CLASS_BOK && this.studentId && this.isp === INDIVIDUAL_STUDENT_PROGRESS;
			},
			printLogoSrc() {
				return assetUrl('/assets/dist/images/rw-bw.jpeg');
			},
			currentResearchAssessment() {
				const researchAssessments = this.researchAssessments;

				return researchAssessments && researchAssessments[0];
			},
			sraButterBarContent() {
				const vabka = 'Vocabulary & Background Knowledge Assessment is active';
				const va = 'Vocabulary Assessment is active';
				const bka = 'Background Knowledge Assessment is active';
				const waata = 'Write About a Topic Assessment is active';

				const content = {
					vabka,
					'vabka-post-assessment': vabka,
					waata,
					'waata-post-assessment': waata,
					va,
					'va-post-assessment': va,
					bka,
					'bka-post-assessment': bka
				};

				return content[this.currentResearchAssessment.type.split(/-\d/)[0]];
			}
		},
		watch: {
			assignments(val) {
				if (val) {
					this.setFilteredAssignments();
				}
			},
			assignmentListTab(val) {
				this.setFilteredAssignments();
			},
			filterClassCode(val) {
				if (val) {
					this.filteredAssignments = null;
					fetchRoster(val);
					this.getAssignments();
					this.fetchGroups();
					getSKIAssignments(val);
					teacherGetStudentResearchAssessments(val);
				} else if (!val && this.filterClassChoices.length && this.filterClassChoices[0].value) {
					model.set('classCode', this.filterClassChoices[0].value);
				}
			},
			currentRoster(roster) {
				this.studentNames = utilities.unambiguousStudentLabels(roster);
			}
		},
		mounted() {
			let seeAll = model.get('seeAll');
			const assignmentTab = this.assignmentTab;

			if (seeAll) {
				this.studentProgressMonth = 'all';
				model.set('studentProgressMonthIndividual', 'all');
				model.set('bigBokMonth', 'all');
				model.remove('seeAll');
			} else if (!this.studentProgressMonth) {
				this.studentProgressMonth = this.progressMonthList[0].value;
			}

			if (!assignmentTab) {
				this.assignmentTab = ASSIGNMENTS;
			} else if (new Set([STATUS_ACTIVE, STATUS_DONE, STATUS_FUTURE]).has(assignmentTab)) {
				this.assignmentListTab = assignmentTab;
				this.assignmentTab = ASSIGNMENTS;
			}

			if (!this.filterClassCode) {
				model.set('classCode', '');
			}

			this.studentId = this.studentId || '';

			this.$subscribe(ASSIGNMENT_ACTION_BEGIN, () => (this.actionInProgress = true));
			this.$subscribe(ASSIGNMENT_ACTION_END, () => {
				this.actionInProgress = false;
				this.getAssignments();
			});

			this.resultsHash = model.get('studentProgressHash');
			model.remove('studentProgressHash');

			getCVAAssignments();
			this.reloadData();

			this.$emit('data-ready');
		},
		methods: {
			reloadData(forceAssignmentReload) {
				let prms = Q(true);
				let classCode = this.filterClassCode;

				if (!this.roster && this.filterClassCode) {
					prms.then(fetchRoster(classCode));
				}
				if (!classCode || !this.filterClassChoices.length) {
					prms.then(this.getClasses);
				}
				if (forceAssignmentReload) {
					prms.then(this.getAssignments).then(() => teacherGetStudentResearchAssessments(classCode));
				}

				return prms;
			},
			getClasses() {
				this.classByCode = {};
				this.classesLoading = true;

				return classService
					.getClasses()
					.then(classes => {
						var filterClassChoices = [];
						if (classes.length) {
							classes = classes.filter(c => {
								if (c.status !== CLASS_STATUS_MAP.archived) {
									this.classByCode = _.assign({}, this.classByCode, {
										[c.classCode]: c
									});
									filterClassChoices.push({
										value: c.classCode,
										text: c.name
									});
									return true;
								}
								return false;
							});
						}

						if (filterClassChoices.length) {
							filterClassChoices.sort(function (a, b) {
								let nameA = (a.text || '').toLowerCase();
								let nameB = (b.text || '').toLowerCase();
								if (nameA < nameB) {
									return -1;
								} else if (nameA > nameB) {
									return 1;
								} else {
									return 0;
								}
							});

							this.filterClassChoices = filterClassChoices;
						} else {
							this.filterClassChoices = [
								{
									value: '',
									text: 'No Classes'
								}
							];
						}

						model.set('classCode', this.filterClassCode || filterClassChoices[0].value);
						this.setFilteredAssignments();
					})
					.finally(() => (this.classesLoading = false));
			},
			getAssignments() {
				let classCode = this.filterClassCode;
				this.assignmentsLoading = true;
				return fetchAssignments(classCode).finally(() => {
					this.assignmentsLoading = false;
					this.assignmentsLoaded = true;
				});
			},
			setFilteredAssignments() {
				let assignmentsList = [];
				const assignments = this.assignments || [];

				assignments.forEach(a => {
					//	Check class code
					if (this.filterClassCode !== '' && this.filterClassCode !== a.classCode) {
						return;
					}

					if (this.assignmentListTab === a.status) {
						a.newStatus = '';
						a.confirmed = false;
						assignmentsList.push(a);
					}
				});

				if (this.assignmentListTab === 'f') {
					assignmentsList.sort((a, b) => a.startDate - b.startDate);
				} else {
					assignmentsList.sort((a, b) => {
						// Bug fix for older articles (pre Aug 2016)
						// that don't have start date.
						// This essentially sets the startDate = creationTimestamp
						// for those articles.
						var comparatorA = a.startDate || a.creationTimestamp;
						var comparatorB = b.startDate || b.creationTimestamp;

						var startDate = comparatorB - comparatorA;
						if (startDate === 0) {
							return b.creationTimestamp - a.creationTimestamp;
						}
						return startDate;
					});
				}

				this.filteredAssignments = assignmentsList;
			},
			onAssignmentAction(option, assignment) {
				this.targetAssignmentProduct = assignment.productId;
				assignmentUtilities.takeAssignmentAction(option.value, assignment, this.roster[this.filterClassCode]);
			},
			backToResults() {
				let resultsHash = this.resultsHash;
				let url = resultsHash ? resultsHash.url : null;

				if (resultsHash && url) {
					delete resultsHash.url;
					page(url);
					model.set(resultsHash);
					this.resultsHash = null;
				}
			},
			backToStudentProgress() {
				model.remove('studentId');
				model.remove('isp');
			},
			changeStudent(studentIndex, event) {
				const suid = this.currentRosterKeys[studentIndex];

				this.studentId = suid;

				fireEvent(event, {
					loc: 'isp',
					cc: this.filterClassCode,
					suid
				});
			},
			previousStudent(studentIndex) {
				this.changeStudent(studentIndex, PREVIOUS_STUDENT);
			},
			nextStudent(studentIndex) {
				this.changeStudent(studentIndex, NEXT_STUDENT);
			},
			cancelCVA(cva) {
				this.actionInProgress = true;
				surveyUtilities.deleteCVAAssignment(cva.cc).then(() => {
					this.actionInProgress = false;
					utilities.closeModals();
				});
			},
			printThis() {
				let section = this.studentId ? 'INDIVIDUAL_STUDENT_PROGRESS' : 'STUDENT_PROGRESS';
				let sid = this.studentId || undefined;
				let subsection = this.studentProgressTab || undefined;
				let cc = this.filterClassCode;

				fireEvent(PRINT, {
					section,
					subsection,
					sid,
					cc
				});
				window.print();
			},
			setClassCode(event) {
				let classCode = event && event.target ? event.target.value : null;
				if (classCode && classCode.length) {
					model.set('classCode', classCode);
					fetchRoster(classCode);
				}
			},
			generateAssignmentLink(aid) {
				return '/assignment/' + this.filterClassCode + '/' + aid + '/class-results';
			},
			onBigBokProductClick(entry) {
				let url = this.generateAssignmentLink(entry.aid);

				model.set('studentProgressHash', {
					assignmentTab: this.assignmentTab,
					studentProgressTab: this.studentProgressTab,
					classCode: this.filterClassCode,
					studentId: this.studentId,
					isp: INDIVIDUAL_STUDENT_PROGRESS
				});

				page(url);
				model.set({
					resultsTab: 'classresults',
					student: this.studentId,
					assessmentTab: 'bookofknowledge'
				});
			},
			exportData() {
				let className = this.className;
				let studentProgressMonth = this.studentProgressMonth;
				let studentProgressExportData = model.get('studentProgressExportData');
				let month = null;
				let data = [];
				let tab = this.studentProgressTab || undefined;
				let cc = this.filterClassCode;

				if (studentProgressExportData && studentProgressExportData.length) {
					if (studentProgressMonth !== 'all') {
						month = studentProgressUtilities.months[studentProgressMonth];
					} else {
						month = 'All';
					}
					let lastPosition = studentProgressExportData[0].length - 1;

					let classNameMeta = [];
					classNameMeta[0] = 'Class';
					classNameMeta[lastPosition] = className;

					let monthMeta = [];
					monthMeta[0] = 'Time Period';
					monthMeta[lastPosition] = month;

					let metaData = [[], classNameMeta, monthMeta, []];

					data.push(studentProgressExportData[0]);
					data = data.concat(metaData).concat(studentProgressExportData.slice(1));

					downloadCSV(data, 'ReadWorks_' + className + '.csv');
					fireEvent(EXPORT_CSV, {
						cc,
						tab
					});
				}
			},
			exportISPData() {
				const ispExportData = this.ispExportData;
				const cc = this.filterClassCode;
				const className = this.className.replaceAll(' ', '_');
				const sid = this.studentId;
				const studentName = this.progressStudentName.replace(' ', '_');

				if (ispExportData) {
					downloadCSV(ispExportData, `ReadWorks_${studentName}_${className}_Progress.csv`);
					fireEvent(EXPORT_CSV, {
						cc,
						tab: 'isp',
						sid
					});
				}
			},
			fetchGroups() {
				let cc = this.filterClassCode;
				let prms = Q();
				this.loadingGroups = true;
				if (cc) {
					prms = studentGroupUtilities.fetchGroups(cc).finally(() => (this.loadingGroups = false));
				}
				return prms;
			},
			onMonthSelect(event) {
				this.studentProgressMonth = event.target.value;
			},
			openSRAStatusModal() {
				utilities.openModal('#sra-status-modal');
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'mixins/flexbox';
	@import 'mixins/media';

	#main-application.whiteboard-view {
		main.main.assignments .main-header {
			display: none;
		}
	}

	main.main.assignments {
		.main-header-back {
			margin-top: 0rem;
			cursor: pointer;
		}

		.main-header .nav-container.clear {
			clear: both;
		}

		.main-header-filters {
			float: right;
		}

		.main-header .title-container {
			display: flex;
			align-items: baseline;
			flex-wrap: wrap;

			.main-header-title,
			.class-select {
				float: none;
				clear: none;
			}

			.main-header-title {
				margin-right: 1rem;
			}

			.class-select {
				position: relative;
			}
		}

		.form .input-select.-dropdown.-dropdown-status {
			border: 1px solid #dcdee0;
			min-height: inherit;
		}

		.form .input-select.-dropdown {
			&.-dropdown-status select {
				padding: 4px;
			}

			select[name='status'] {
				width: 200px;
			}
		}

		.form .input-select.-dropdown.-dropdown-status:after {
			right: 5px;
			top: 0.87em;
		}

		.main-content {
			padding: 0rem 0rem 1.5rem 0rem;

			section.assignments .big-book-of-knowledge {
				margin-top: 1rem;
			}

			.bbok-title-container {
				@include flexbox();
				@include align-items(center);

				h2 {
					@include flex(1);
				}
			}
		}

		.cva-notification,
		.ski-notification {
			display: block;

			.cancel,
			.ski-results {
				float: right;
			}

			.cancel {
				color: $c-slate-dark;
				cursor: pointer;
				text-decoration: underline;

				&:hover {
					color: $c-slate-dark;
				}
			}
		}

		.sra-butter-bar {
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
			width: 100%;
		}
	}

	#modal-show-results,
	#modal-close-assignment {
		.dismiss {
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	@media print {
		main.main.assignments {
			margin-bottom: 0rem;

			div.wrapper {
				width: 100%;
				max-width: none;
			}

			header.main-header {
				border-bottom: none;
				padding: 0rem;

				> .wrapper {
					@include flexbox();
					@include align-items(center);

					.print-logo.print-only {
						@include flex(1);
					}

					h1.main-header-title {
						font-size: 14px !important;
						text-align: right;

						.student-select.print-only > .class-name.label {
							font-size: 12px !important;
							margin-right: 0rem;
						}
					}
				}
			}

			div.main-content.wrapper {
				padding-bottom: 0rem;

				section.assignments,
				section.class-results {
					margin-bottom: 0rem; // Css for results grids
				}
			}
		}
	}
</style>
<template>
	<main
		id="content"
		class="main assignments">
		<header
			class="main-header"
			:class="{ '-has-nav': !studentId }">
			<div class="wrapper">
				<div class="print-logo print-only">
					<img
						style="width: 110px; height: 17px"
						:src="printLogoSrc" />
				</div>
				<template v-if="studentId">
					<span
						v-if="resultsHash"
						@click="backToResults"
						class="main-header-back print-hidden"
						>&larr; Back to Results</span
					>
					<span
						v-if="!resultsHash"
						@click="backToStudentProgress"
						class="main-header-back print-hidden"
						>&larr; Back to Student Progress</span
					>
				</template>
				<div
					class="main-header-filters form print-hidden"
					v-if="studentId && currentRosterKeys">
					<pagination
						class="print-hidden"
						label="Student"
						:page="currentStudentIndex"
						:total="currentRosterKeys.length"
						:page-size="1"
						:show-arrow-text="false"
						allow-wrap
						variant="minimal"
						:on-previous-page="previousStudent"
						:on-next-page="nextStudent"></pagination>
				</div>
				<div class="clearfix"></div>
				<div class="title-container">
					<h1 class="main-header-title">
						<template v-if="!studentId"> Assignments &amp; Progress </template>
						<template v-if="studentId">
							{{ progressStudentName }}
						</template>
						<div
							v-if="currentRosterKeys && className"
							class="print-only student-select">
							<span class="class-name label">{{ className }}</span>
						</div>
					</h1>
					<div
						v-if="!studentId"
						class="main-header-filters form print-hidden class-select">
						<div
							class="input-select -last"
							:class="{ disabled: !filterClassCode }">
							<select
								@change="setClassCode"
								class="assignments-class-select"
								:disabled="!filterClassCode">
								<option
									v-for="o in filterClassChoices"
									:key="o.value"
									:value="o.value"
									:selected="o.value === filterClassCode">
									{{ o.text }}
								</option>
							</select>
						</div>
					</div>
					<div
						v-else-if="currentRosterKeys && className"
						class="print-hidden">
						<span class="class-name label">{{ className }}</span>
					</div>
				</div>
				<div
					class="nav-container"
					:class="{
						clear: !studentId
					}">
					<tab-container
						model-key="assignmentTab"
						:default-tab="ASSIGNMENTS"
						class="print-hidden"
						v-if="!studentId">
						<button :data-tab="ASSIGNMENTS">Assignments</button>
						<button :data-tab="CLASS_BOK">Class Book of Knowledge</button>
						<button :data-tab="RMS">Reading Mindset Snapshot</button>
						<button :data-tab="STUDENT_PROGRESS">Student Progress</button>
					</tab-container>
				</div>
			</div>
		</header>
		<div
			class="main-content wrapper"
			ref="assignmentTabPanel"
			:data-panel="assignmentTab">
			<section class="assignments">
				<div
					v-if="cvaAssignment"
					class="cva-notification butter-bar">
					<span>Your Vocabulary Quest<sup>beta</sup> is scheduled for {{ cvaDate }}.</span>
					<span
						class="cancel"
						data-modal="#modal-confirm-cva-delete"
						>Cancel</span
					>
				</div>
				<div class="assignments-content">
					<div
						v-if="studentId"
						class="assignments-content-header">
						<tab-container
							class="print-hidden"
							variant="pill-tabs"
							model-key="isp">
							<button
								:data-tab="INDIVIDUAL_STUDENT_PROGRESS"
								:class="{ active: isp === INDIVIDUAL_STUDENT_PROGRESS }">
								Individual Student Progress
							</button>
							<button
								:data-tab="BBOK_PROGRESS"
								:class="{ active: isp === BBOK_PROGRESS }">
								Big Book of Knowledge
							</button>
							<button
								:data-tab="LIBRARY_PROGRESS"
								:class="{ active: isp === LIBRARY_PROGRESS }">
								Library Activity
							</button>
						</tab-container>
						<div class="grid-tools form margin-bottom-small print-hidden">
							<div
								v-if="studentId"
								class="input-select slim date-select">
								<select
									class="input"
									:value="studentProgressMonth"
									@change="onMonthSelect">
									<option
										v-for="month in progressMonthList"
										:key="month.value"
										:value="month.value">
										{{ month.text }}
									</option>
								</select>
							</div>
							<button
								class="button -small blue-outline export-progress"
								v-if="showIndividualStudentProgress"
								:disabled="!ispExportData"
								@click="exportISPData"
								aria-label="export">
								<i class="fa-solid fa-up-right-from-square fa-lg"></i>
							</button>
							<button
								class="button -small blue-outline print-progress"
								@click="printThis"
								aria-label="Print">
								<i class="fa-solid fa-print fa-lg"></i>
							</button>
						</div>
					</div>
					<template v-if="assignmentTab === ASSIGNMENTS && !studentId">
						<tab-container
							model-key="assignmentListTab"
							:default-tab="STATUS_ACTIVE"
							variant="pill-tabs"
							class="print-hidden">
							<button :data-tab="STATUS_ACTIVE">Current Assignments</button>
							<button :data-tab="STATUS_FUTURE">Future Assignments</button>
							<button :data-tab="STATUS_DONE">Past Assignments</button>
						</tab-container>
						<a
							v-if="
								skiAssignments &&
								skiAssignments.length &&
								!isLoading &&
								((assignmentListTab === STATUS_ACTIVE && !skiScheduledDate && !skiAssignment.ed) ||
									(assignmentListTab === STATUS_FUTURE && skiScheduledDate) ||
									(assignmentListTab === STATUS_DONE && !skiScheduledDate && skiAssignment.ed))
							"
							:href="`#!classCode:${filterClassCode}/assignmentTab:${RMS}/assignmentListTab:${assignmentListTab}`"
							class="ski-notification butter-bar">
							<span v-if="skiScheduledDate">Your Reading Mindset Snapshot is scheduled for {{ skiScheduledDate | dateShort }}</span>
							<span v-if="!skiScheduledDate && !skiAssignment.ed"
								>Your Reading Mindset Snapshot is currently underway
								<span class="ski-results">View Results</span>
							</span>
							<span v-if="!skiScheduledDate && skiAssignment.ed"
								>Your Reading Mindset Snapshot ended on {{ skiAssignment.ed | dateShort }}
								<span class="ski-results">View Results</span>
							</span>
						</a>
						<butter-bar
							v-if="currentResearchAssessment"
							class="sra-butter-bar">
							<div
								class="sra-text margin-right-medium"
								v-text="sraButterBarContent"></div>
							<div class="sra-button-container">
								<button
									class="button -link -link-standard sra-butter-bar-action"
									@click="openSRAStatusModal">
									View Student Submission Status
								</button>
							</div>
						</butter-bar>
						<assignments-list
							:assignments="filteredAssignments"
							:is-loading="isLoading"
							:clazz="currentClass"
							:filter-status="assignmentListTab"
							:on-option-click="onAssignmentAction"
							:cva-assignment="cvaAssignment"
							:ski-assignment="skiAssignment"
							ref="assignmentListTabPanel"
							:data-panel="assignmentListTab">
						</assignments-list>
					</template>
					<class-book-of-knowledge
						v-if="assignmentTab === CLASS_BOK"
						:is-loading="isLoading"
						:assignments="assignments"
						:roster="currentRoster">
					</class-book-of-knowledge>
					<student-knowledge-inventory v-if="assignmentTab === RMS"></student-knowledge-inventory>
					<student-progress
						v-if="assignmentTab === STUDENT_PROGRESS && assignmentTab !== CLASS_BOK && !studentId"
						:current-class="currentClass"
						:export-data="exportData"
						:print-view="printThis">
					</student-progress>
					<individual-student-progress
						v-if="showIndividualStudentProgress"
						:current-class="currentClass"
						:student-name="progressStudentName"
						:month-list="progressMonthList"
						ref="ispPanel"
						:data-panel="INDIVIDUAL_STUDENT_PROGRESS">
					</individual-student-progress>
					<big-book-of-knowledge
						v-if="assignmentTab !== CLASS_BOK && studentId && isp === BBOK_PROGRESS"
						:assignments="assignments"
						:loading="isLoading"
						:current-class="currentClass"
						:on-title-click="onBigBokProductClick"
						:student-name="progressStudentName"
						:suid="progressStudent && progressStudent.uid"
						:bok-note-editable="true"
						:hide-month-picker="true"
						ref="ispPanel"
						:data-panel="BBOK_PROGRESS">
					</big-book-of-knowledge>
					<student-library-individual-results
						v-if="assignmentTab !== CLASS_BOK && studentId && isp === LIBRARY_PROGRESS"
						:assignments="assignments"
						:loading="isLoading"
						:current-class="currentClass"
						:on-title-click="onBigBokProductClick"
						:student-name="progressStudentName"
						:suid="progressStudent && progressStudent.uid"
						ref="ispPanel"
						:data-panel="LIBRARY_PROGRESS">
					</student-library-individual-results>
				</div>
			</section>
		</div>

		<div
			id="modal-confirm-cva-delete"
			class="modal"
			data-modal-window>
			<div class="modal-container">
				<div class="modal-window">
					<div class="modal-window-wrap">
						<button
							class="modal-close"
							data-modal-close>
							<span aria-hidden="true">&times;</span><span class="hidden-visually"> Close</span>
						</button>
						<section class="modal-content">
							<header class="modal-content-header">
								<h2>Cancel Vocabulary Quest<sup>beta</sup></h2>
							</header>
							<div class="modal-content-main -centered">
								<h3>Are you sure you want to cancel the Vocabulary Quest<sup>beta</sup> that is scheduled for {{ cvaDate }}?</h3>
								<p>
									<a
										class="button -outline"
										data-modal-close>
										Do Not Delete
									</a>
									<a
										class="button -danger"
										@click="cancelCVA(cvaAssignment)">
										Delete
									</a>
								</p>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
		<!-- end #modal-confirm-cva-delete -->
		<assignment-modal
			show-article-title
			:product-id="targetAssignmentProduct"></assignment-modal>
		<show-results-modal></show-results-modal>
		<change-deadline-modal></change-deadline-modal>
		<assignment-delete-modal></assignment-delete-modal>
		<assignment-close-modal></assignment-close-modal>
		<sra-status-modal
			v-if="currentResearchAssessment"
			:assessment="currentResearchAssessment"
			:roster="currentRoster"></sra-status-modal>
	</main>
</template>
